
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom' // Added for navigation
import { ChevronDown, MessageSquare, Bell, UserCheck, GraduationCap, CreditCard, Target, ChevronRight } from 'lucide-react'
import Image1 from "../../assets/education.webp"
import Image2 from "../../assets/education2.webp"

// SECTION 3 IMAGES
import ImageStep1 from "../../assets/education3.webp"
import ImageStep2 from "../../assets/education4.webp"
import ImageStep3 from "../../assets/education5.webp"

const Education = () => {
    const [openIndex, setOpenIndex] = useState(0);
    const navigate = useNavigate(); // Initialize the navigate function
    const handleSignupClick = () => {
    navigate("/login?mode=signup");
    };
    const solutions = [
        { title: "Automated Enrollment Alerts", description: "Streamline the admission process by sending automated SMS updates to students regarding application status and document verification." },
        { title: "Attendance & Academic Alerts", description: "Instantly notify parents about student absences or share monthly progress reports directly to their mobile phones." },
        { title: "Fee Payment Reminders", description: "Reduce late payments by sending personalized SMS links for fee installments, ensuring smooth cash flow." },
        { title: "Instant Campus Announcements", description: "Broadcast emergency alerts, holiday notices, or event invitations to the entire student body in seconds." },
        { title: "Placement & Career Updates", description: "Keep final-year students informed about upcoming campus drives and interview schedules." }
    ];

    // Split tags into two rows for a more professional look
    const row1 = ["Fill Admission Form", "Submit Past Certificates", "Pre-Enrollment Counseling", "Course Discovery", "Enrollment", "Fee Submission"];
    const row2 = ["Assessments", "Performance Report", "Doubt Clarification", "Upskill Recommendation", "Learning Guidance", "Curriculum Progress"];

    return (
        <main className="font-sans antialiased overflow-x-hidden">
            {/* Premium Animation Styles */}
            <style>
                {`
                    @keyframes scroll-left {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    @keyframes scroll-right {
                        0% { transform: translateX(-50%); }
                        100% { transform: translateX(0); }
                    }
                    .animate-scroll-left {
                        display: flex;
                        width: max-content;
                        animation: scroll-left 50s linear infinite;
                    }
                    .animate-scroll-right {
                        display: flex;
                        width: max-content;
                        animation: scroll-right 50s linear infinite;
                    }
                    .pause-on-hover:hover {
                        animation-play-state: paused;
                    }
                `}
            </style>

            {/* SECTION 1: HERO SECTION */}
            <section className="bg-[#EEF2FC] text-white py-36 lg:py-32 overflow-hidden">
                <div className="container mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-center gap-12">
                    <div className="lg:w-7/12 text-left z-10">
                        <p className="text-[#F46653] font-bold tracking-wide mb-4 uppercase text-sm lg:text-base -mt-32">
                            Eurofox education solutions
                        </p>
                        <h1 className="text-5xl lg:text-7xl font-bold tracking-tight mb-8 leading-[1.1] text-slate-900">
                            Shape the future, one <br />
                            <span className="text-slate-900 font-medium text-7xl">student at a time</span>
                        </h1>
                        <p className="text-lg lg:text-xl text-slate-700 font-normal max-w-2xl mb-10 leading-relaxed">
                            Boost student lifecycle outcomes with AI-enabled engagement strategies that grow enrollment, improve graduation rates, and increase contributions.
                        </p>
                        <div className="flex flex-wrap items-center gap-6">
                            {/* Logic 1: Navigate to /contact */}
                            <button 
                                onClick={() => navigate('/contact')}
                                className="inline-flex items-center gap-2 px-6 py-3 text-white bg-slate-900 rounded-lg transition duration-300 shadow-md "
                            >
                                Request a Demo
                                <ChevronRight className="w-5 h-5 animate-bounce" />
                            </button>
                        </div>
                    </div>
                    <div className="lg:w-5/12 w-full mt-16 lg:-mt-16">
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full h-[350px] lg:h-[500px]">
                            <img
                                src={Image1}
                                alt="Education Solutions"
                                className="w-full h-full object-cover object-center"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 2: EDTECH PROCESSES */}
            <section className="bg-[#EEF2FC] py-20 lg:py-32 -mt-32">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="text-center max-w-4xl mx-auto mb-16 lg:mb-24">
                        <h2 className="text-4xl lg:text-6xl font-medium text-slate-900 leading-tight">
                            How Eurofox can help <br />
                            <span className="text-[#F46653]">EdTech processes</span>
                        </h2>
                    </div>
                    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                        <div className="lg:w-5/12 w-full">
                            <div className="relative rounded-2xl overflow-hidden shadow-lg w-full h-[300px] lg:h-[530px]">
                                <img
                                    src={Image2}
                                    alt="Eurofox EdTech Process"
                                    className="w-full h-full object-cover object-center"
                                />
                            </div>
                        </div>
                        <div className="lg:w-7/12 w-full space-y-4">
                            {solutions.map((item, index) => (
                                <div key={index} className="bg-white border border-slate-200 rounded-2xl shadow-sm transition-all duration-300">
                                    <button
                                        onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                                        className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors rounded-2xl"
                                    >
                                        <span className="font-medium text-slate-800 text-lg lg:text-xl pr-4">
                                            {item.title}
                                        </span>
                                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`} />
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className="px-6 pb-6 text-slate-600 text-lg leading-relaxed border-t border-slate-50 pt-4">
                                            {item.description}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 3: BUILD BETTER LEARNING */}
            <section className="bg-[#EEF2FC] py-24 lg:py-32 -mt-20">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-6xl font-medium text-slate-900">
                            Build better learning, <span className="text-[#F46653]">one step at a time.</span>
                        </h2>
                    </div>

                    <div className="flex flex-col space-y-12">
                        {/* --- STEP 1 CONTAINER --- */}
                        <div className="step-1-container relative">
                            <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-16">
                                <div className="lg:w-6/12 w-full lg:pr-12">
                                    <h3 className="text-3xl lg:text-4xl font-medium text-slate-900 mb-6">
                                        Simplify and Scale Your Admissions Process
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Drive more qualified leads through AI-powered Voice and WhatsApp campaigns.</p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Reach prospective students faster with automated communication flows.</p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Improve team performance with integrated dialers and CRM systems.</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full lg:w-[350px] flex-shrink-0">
                                    <div className="relative rounded-xl overflow-hidden w-full lg:h-[310px]">
                                        <img src={ImageStep1} alt="Admissions" className="w-full h-full object-cover" />
                                    </div>
                                </div>
                            </div>
                            <div className="hidden lg:block absolute left-1/2 -bottom-12 transform -translate-x-1/2">
                                <div className="w-0.5 h-16 bg-gradient-to-b from-[#F46653]/30 to-transparent"></div>
                            </div>
                        </div>

                        {/* --- STEP 2 CONTAINER --- */}
                        <div className="step-2-container relative lg:-mt-32">
                            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                                <div className="lg:w-6/12 w-full lg:pl-16">
                                    <h3 className="text-3xl lg:text-4xl font-medium text-slate-900 mb-6">
                                        Intelligent Notification System for Education Workflows
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Pre-class reminders delivered at the right time.</p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Automated tuition and payment due alerts.</p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Share exam results and assignment updates instantly.</p>
                                        </div>
                                    </div>
                                </div>
                               <div className="w-full lg:w-[350px] flex-shrink-0">
                                    <div className="relative rounded-xl overflow-hidden w-full lg:h-[310px]">
                                        <img src={ImageStep2} alt="Admissions" className="w-full h-full object-cover" />
                                    </div>
                                </div>
                            </div>
                            <div className="hidden lg:block absolute left-1/2 -bottom-12 transform -translate-x-1/2">
                                <div className="w-0.5 h-16 bg-gradient-to-b from-[#F46653]/30 to-transparent"></div>
                            </div>
                        </div>

                        {/* --- STEP 3 CONTAINER --- */}
                        <div className="step-3-container relative lg:-mt-32">
                            <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-16">
                                <div className="lg:w-6/12 w-full lg:pr-12">
                                    <h3 className="text-3xl lg:text-4xl font-medium text-slate-900 mb-6">
                                        Provide Excellence in Student Education
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Send instant class reminders and academic alerts through Bulk SMS.</p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Broadcast important announcements to all students in one click.</p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-2 h-2 bg-[#F46653] rounded-full mt-2 flex-shrink-0"></div>
                                            <p className="text-slate-700 text-lg leading-relaxed">Improve student response and participation with high-open-rate SMS communication.</p>
                                        </div>
                                    </div>
                                </div>
                                 <div className="w-full lg:w-[350px] flex-shrink-0">
                                    <div className="relative rounded-xl overflow-hidden w-full lg:h-[320px]">
                                        <img src={ImageStep3} alt="Admissions" className="w-full h-full object-cover" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 4: PREMIUM CONVERSATIONAL WORKFLOWS */}
            <section className="bg-[#EEF2FC] py-24 lg:py-32 border-t border-slate-100">
                <div className="container mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl lg:text-6xl font-medium text-slate-900 mb-6 tracking-tight -mt-24">
                        Turn complex student workflows <span className="text-[#F46653]">into</span>
                        <br />
                        <span className="text-[#F46653]">effortless conversations</span>
                    </h2>
                  {/* Scaled down top/bottom margins for mobile */}
        <div className="relative mt-10 mb-10 lg:mt-20 lg:mb-20 overflow-hidden">
            
            {/* Reduced gradient width on mobile so it doesn't cover the whole screen */}
            <div className="absolute inset-y-0 left-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-r from-[#EEF2FC] to-transparent"></div>
            <div className="absolute inset-y-0 right-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-l from-[#EEF2FC] to-transparent"></div>

            {/* Added 'flex' to ensure gap works, adjusted gaps for mobile */}
            <div className="animate-scroll-left flex gap-4 lg:gap-8 mb-4 lg:mb-8">
                {[...row1, ...row1].map((tag, idx) => (
                    <div
                        key={idx}
                        className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                    >
                        {tag}
                    </div>
                ))}
            </div>

            <div className="animate-scroll-right flex gap-4 lg:gap-8">
                {[...row2, ...row2].map((tag, idx) => (
                    <div
                        key={idx}
                        className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                    >
                        {tag}
                    </div>
                ))}
            </div>
        </div>

                    <p className="text-xl lg:text-2xl text-slate-600 font-normal max-w-4xl mx-auto leading-relaxed mb-16">
                        Empower learners and educators with intelligent AI workflows that guide, support, and continuously improve every learning interaction.
                    </p>

                    {/* Logic 2: Navigate to /login */}
                    <button 
                        // onClick={() => navigate('/login')}
                        onClick={handleSignupClick}
                        className="inline-flex items-center gap-3 px-10 py-5 text-white bg-slate-900 rounded-xl transition duration-300 shadow-xl hover:shadow-2xl text-xl font-semibold"
                    >
                        Start Your Learning Journey
                        <Target className="w-6 h-6" />
                    </button>
                </div>
            </section>
        </main>
    )
}

export default Education